import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  RiAddLine,
  RiEdit2Line,
  RiDeleteBin6Line,
  RiSearchLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
} from "react-icons/ri";

import { getSuppliers, deleteSupplier } from "../services/supplierService";
import "./UnitList.css"; // ✅ reuse same CSS as Unit List

const SupplierListLayer = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  // search
  const [search, setSearch] = useState("");

  // pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5); // ✅ NEW

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const res = await getSuppliers();

      if (res.status === "success") {
        setSuppliers(res.data || []);
      } else {
        toast.error(res.message || "Failed to load suppliers");
      }
    } catch {
      toast.error("API Error while loading suppliers");
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this supplier?")) return;

    try {
      const res = await deleteSupplier(id);
      if (res.status === "success") {
        toast.success("Supplier deleted");
        loadSuppliers();
      } else toast.error(res.message);
    } catch {
      toast.error("API Error");
    }
  };

  /* =============================
     SEARCH FILTER
  ============================= */
  const filteredData = useMemo(() => {
    return suppliers.filter((s) =>
      `${s.name} ${s.contact_person} ${s.phone} ${s.email || ""}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [suppliers, search]);

  /* =============================
     PAGINATION LOGIC
  ============================= */
  const effectivePageSize =
    pageSize === "all" ? filteredData.length : pageSize;

  const totalPages =
    pageSize === "all"
      ? 1
      : Math.ceil(filteredData.length / effectivePageSize);

  const paginatedData =
    pageSize === "all"
      ? filteredData
      : filteredData.slice(
          (currentPage - 1) * effectivePageSize,
          currentPage * effectivePageSize
        );

  return (
    <div className="ul-page">
      {/* HEADER */}
      <div className="ul-header">
        <h4>Supplier List</h4>
        <Link to="/add-supplier" className="ul-add-btn">
          <RiAddLine /> Add Supplier
        </Link>
      </div>

      {/* FILTER BAR */}
      <div className="ul-filters">
        {/* SEARCH */}
        <div className="ul-search">
          <RiSearchLine />
          <input
            placeholder="Search supplier..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>

        {/* PAGE SIZE */}
        <div className="ul-pagesize">
          <span>Show</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(
                e.target.value === "all"
                  ? "all"
                  : Number(e.target.value)
              );
              setCurrentPage(1);
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value="all">All</option>
          </select>
          <span>entries</span>
        </div>
      </div>

      {/* TABLE */}
      <div className="ul-table-wrapper">
        <table className="ul-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>GST No</th>
              <th>Phone</th>

              <th>Opening Balance</th>
              <th>Credit Limit</th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center text-muted">
                  No suppliers found
                </td>
              </tr>
            ) : (
              paginatedData.map((s, index) => (
                <tr key={s.id}>
                  <td>
                    {pageSize === "all"
                      ? index + 1
                      : (currentPage - 1) * effectivePageSize + index + 1}
                  </td>
                  <td>{s.name}</td>
                  <td>{s.gst_no}</td>
                  <td>{s.phone}</td>
                  <td>
                    {s.opening_balance}{" "}
                    <span className="text-muted">
                      ({s.opening_balance_type})
                    </span>
                  </td>
                  <td>{s.credit_limit}</td>
                  <td className="ul-actions">
                    <Link
                      to={`/edit-supplier/${s.id}`}
                      className="ul-btn edit"
                    >
                      <RiEdit2Line />Edit
                    </Link>
                    <button
                      className="ul-btn delete"
                      onClick={() => handleDelete(s.id)}
                    >
                      <RiDeleteBin6Line />Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {pageSize !== "all" && totalPages > 1 && (
        <div className="ul-pagination">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            <RiArrowLeftSLine />
          </button>

          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              className={currentPage === i + 1 ? "active" : ""}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}

          <button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            <RiArrowRightSLine />
          </button>
        </div>
      )}
    </div>
  );
};

export default SupplierListLayer;
